import { useEffect, useRef, useState } from 'react';
import { api, type Brand, type Defaults, type Recording, type ScreenTake } from '../lib/api';
import { clock, lineAt, transcriptText } from '../lib/recordings';
import { episodeFromTake } from '../lib/cutEpisode';

// Spyglass: the screen-recording lab. Sources in — screen takes recorded on
// the Record screen with the camera in a bubble, or uploaded — and assets
// out: the recording as a library file with a poster, its transcript in
// lines, and a page on the door anyone with the link can watch. What Loom
// does, on the creator's own platform, with the take still there to cut an
// episode from.
export function Spyglass({ brand, onRecord, onOpenSources, defaults, onOpenCard }: { brand: Brand | null; onRecord: () => void; onOpenSources: () => void; defaults?: Defaults; onOpenCard?: (id: string) => void }) {
  const [takes, setTakes] = useState<ScreenTake[]>([]);
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [open, setOpen] = useState<Recording | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const load = () => Promise.all([api.spyglassTakes(brand?.id ?? null), api.recordings(brand?.id ?? null)])
    .then(([t, r]) => {
      setTakes(t); setRecordings(r);
      // 155: shared the moment it is ready, when the workspace's defaults say so — once, and only for a build started here.
      if (defaults?.shareRecordings) for (const x of r) if (x.status === 'ready' && !x.shared && started.current.has(x.id)) { started.current.delete(x.id); void api.updateRecording(x.id, { shared: true }).then((n) => setRecordings((rs) => rs.map((y) => (y.id === n.id ? { ...y, ...n } : y)))).catch(() => undefined); }
    }).catch((e: Error) => setError(e.message));
  const started = useRef<Set<string>>(new Set());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load(); }, [brand?.id]);
  // A building recording is watched until it is ready or says why not.
  useEffect(() => {
    if (!recordings.some((r) => r.status === 'building')) return;
    const t = window.setInterval(() => void load(), 4000);
    return () => window.clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordings]);

  const build = async (take: ScreenTake) => {
    setBusy(take.id); setError(null);
    try { const r = await api.buildRecording(take.id); started.current.add(r.id); setRecordings((rs) => [r, ...rs]); setTakes((ts) => ts.filter((t) => t.id !== take.id)); }
    catch (e) { setError((e as Error).message); } finally { setBusy(null); }
  };
  const show = async (r: Recording) => {
    setBusy(r.id); setError(null);
    try { setOpen(await api.recording(r.id)); } catch (e) { setError((e as Error).message); } finally { setBusy(null); }
  };
  const patch = async (r: Recording, p: { name?: string; shared?: boolean }) => {
    setBusy(r.id); setError(null);
    try {
      const next = await api.updateRecording(r.id, p);
      setRecordings((rs) => rs.map((x) => (x.id === r.id ? { ...x, ...next } : x)));
      if (open?.id === r.id) setOpen({ ...open, ...next });
    } catch (e) { setError((e as Error).message); } finally { setBusy(null); }
  };
  const remove = async (r: Recording) => {
    setBusy(r.id); setError(null);
    try { await api.deleteRecording(r.id); setRecordings((rs) => rs.filter((x) => x.id !== r.id)); if (open?.id === r.id) setOpen(null); void load(); }
    catch (e) { setError((e as Error).message); } finally { setBusy(null); }
  };
  const copy = (r: Recording) => {
    if (!r.shareUrl) return;
    void navigator.clipboard?.writeText(r.shareUrl).then(() => { setCopied(r.id); window.setTimeout(() => setCopied(null), 1500); }).catch(() => undefined);
  };

  return (
    <div data-tool="spyglass" style={{ display: 'grid', gap: 14 }}>
      <div className="row" style={{ alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Spyglass</h2>
        <span className="sub-dim">Screen takes in. Recordings out: a file in Assets with a poster, the transcript, and a page anyone with the link can watch.</span>
        <span style={{ flex: 1 }} />
        <button type="button" className="btn pri" data-tool="spyglass-record" onClick={onRecord}>● Record your screen</button>
      </div>
      {error && <p className="err" role="alert">{error}</p>}

      <section style={{ display: 'grid', gap: 8 }} data-move="spyglass-build">
        <div className="row" style={{ gap: 8, alignItems: 'baseline' }}>
          <h3 style={{ margin: 0, fontSize: 14 }}>Takes to build</h3>
          <span className="sub-dim">screen takes in Sources that are not recordings yet</span>
          <button type="button" className="btn tiny" onClick={onOpenSources}>Sources</button>
        </div>
        {takes.length === 0 ? <p className="sub-dim" style={{ margin: 0 }}>Nothing waiting. Record your screen, or upload a screen recording to Sources as a screen take.</p> : (
          <div className="row wrap" style={{ gap: 8 }}>
            {takes.map((t) => (
              <div key={t.id} className="card" style={{ display: 'grid', gap: 4, padding: '8px 10px', minWidth: 220 }}>
                <strong style={{ fontSize: 13 }}>{t.name}</strong>
                <span className="sub-dim">{clock(t.durationMs)}{t.hasWords ? ' · transcribed' : ' · no words yet'}</span>
                <button type="button" className="btn tiny pri" disabled={busy === t.id} onClick={() => void build(t)} title="File it as a recording: the file with a poster, the transcript, a page to share">
                  {busy === t.id ? 'Building…' : 'Build the recording'}
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section style={{ display: 'grid', gap: 8 }} data-move="spyglass-share">
        <h3 style={{ margin: 0, fontSize: 14 }}>Recordings</h3>
        {recordings.length === 0 ? <p className="sub-dim" style={{ margin: 0 }}>No recordings yet.</p> : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
            {recordings.map((r) => (
              <div key={r.id} className="card" data-tool="recording" style={{ display: 'grid', gap: 6, padding: 10, borderColor: open?.id === r.id ? 'var(--accent, #e0b100)' : undefined }}>
                <button type="button" onClick={() => void show(r)} disabled={r.status !== 'ready' || busy === r.id}
                        style={{ all: 'unset', cursor: r.status === 'ready' ? 'pointer' : 'default', aspectRatio: '16 / 9', background: '#101418', borderRadius: 6, display: 'grid', placeItems: 'center', position: 'relative', overflow: 'hidden' }}
                        title={r.status === 'ready' ? 'Watch it' : r.status === 'building' ? 'Being built' : r.error ?? 'Could not be built'}>
                  <Poster id={r.posterAssetId} ready={r.status === 'ready'} />
                  <span style={{ position: 'absolute', right: 6, bottom: 6, background: 'rgba(0,0,0,.7)', color: '#fff', fontSize: 11, padding: '1px 6px', borderRadius: 4 }}>{clock(r.durationMs)}</span>
                  {r.status !== 'ready' && <span style={{ position: 'absolute', left: 6, top: 6, background: r.status === 'error' ? '#dc2626' : 'rgba(0,0,0,.7)', color: '#fff', fontSize: 11, padding: '1px 6px', borderRadius: 4 }}>{r.status === 'building' ? 'building…' : 'error'}</span>}
                </button>
                <strong style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.name}>{r.name}</strong>
                <div className="row wrap" style={{ gap: 4 }}>
                  <button type="button" className={`btn tiny${r.shared ? ' pri' : ''}`} disabled={r.status !== 'ready' || busy === r.id} onClick={() => void patch(r, { shared: !r.shared })}
                          title={r.shared ? 'Shared: anyone with the link can watch. Click to close the page.' : 'Make a page on the door that anyone with the link can watch'}>{r.shared ? 'Shared' : 'Share'}</button>
                  {r.shared && r.shareUrl && <button type="button" className="btn tiny" onClick={() => copy(r)} title={r.shareUrl}>{copied === r.id ? 'Copied' : 'Copy link'}</button>}
                  {r.videoAssetId && r.status === 'ready' && <button type="button" className="btn tiny" onClick={() => void api.assetUrl(r.videoAssetId!).then((u) => window.open(u.url, '_blank', 'noopener')).catch((e: Error) => setError(e.message))} title="The file, from Assets">Download</button>}
                  <span style={{ flex: 1 }} />
                  <button type="button" className="btn tiny" disabled={busy === r.id} onClick={() => void remove(r)} title="Remove the recording; its file stays in Assets">×</button>
                </div>
                {r.status === 'error' && r.error && <span className="err" style={{ fontSize: 12 }}>{r.error}</span>}
              </div>
            ))}
          </div>
        )}
      </section>

      {open && <RecordingPlayer recording={open} onClose={() => setOpen(null)} onRename={(name) => void patch(open, { name })}
                               onEpisode={onOpenCard ? async () => {
                                 // T234: an episode with a scene per chapter, read by the default talent.
                                 const speaker = defaults?.talent ?? Object.keys(brand?.voices ?? {})[0] ?? 'Host';
                                 try { onOpenCard(await episodeFromTake({ sourceId: open.sourceId, name: open.name, brandId: open.brandId ?? brand?.id ?? null, speaker, chapters: open.chapters ?? [] })); }
                                 catch (e) { setError((e as Error).message); }
                               } : undefined}
                               onRead={(r) => { setOpen({ ...open, summary: r.summary, chapters: r.chapters, readAt: new Date().toISOString() }); setRecordings((rs) => rs.map((x) => (x.id === open.id ? { ...x, summary: r.summary, chapters: r.chapters } : x))); }} />}
    </div>
  );
}

function Poster({ id, ready }: { id: string | null; ready: boolean }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let live = true; setUrl(null);
    if (!id || !ready) return;
    api.assetThumbnail(id).then((u) => { if (live) setUrl(u); }).catch(() => undefined);
    return () => { live = false; };
  }, [id, ready]);
  return url ? <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span className="sub-dim">▶</span>;
}

/** The recording, playing, with its transcript beside it: the line the playhead is in is lit; a click seeks. */
export function RecordingPlayer({ recording, onClose, onRename, onRead, onEpisode }: { recording: Recording; onClose: () => void; onRename: (name: string) => void; onRead?: (r: { title: string; summary: string; chapters: { at: number; title: string }[] }) => void; onEpisode?: () => Promise<void> }) {
  const video = useRef<HTMLVideoElement>(null);
  const [t, setT] = useState(0);
  const [name, setName] = useState(recording.name);
  const [reading, setReading] = useState(false);
  const [suggested, setSuggested] = useState<string | null>(null);
  const [readErr, setReadErr] = useState<string | null>(null);
  const [cutting, setCutting] = useState(false);
  const seek = (at: number) => { if (video.current) { video.current.currentTime = at; void video.current.play(); } };
  const read = async () => {
    setReading(true); setReadErr(null);
    try { const r = await api.readRecording(recording.id); onRead?.(r); if (r.title && r.title !== recording.name) setSuggested(r.title); }
    catch (e) { setReadErr((e as Error).message); } finally { setReading(false); }
  };
  useEffect(() => setName(recording.name), [recording.name]);
  const cur = lineAt(recording.transcript, t);
  return (
    <section data-tool="recording-player" style={{ display: 'grid', gap: 8, border: '1px solid var(--line)', borderRadius: 10, padding: 12 }}>
      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
        <input value={name} maxLength={120} onChange={(e) => setName(e.target.value)} onBlur={() => { if (name.trim() && name.trim() !== recording.name) onRename(name.trim()); }} style={{ fontWeight: 600, flex: 1 }} aria-label="Recording name" />
        <button type="button" className="btn tiny" data-tool="recording-read" disabled={reading} onClick={() => void read()} title="The director reads what was said: a title, a summary for the page, chapters to jump to">{reading ? 'Reading…' : recording.readAt ? 'Read it again' : 'Ask the director to read it'}</button>
        {onEpisode && (recording.chapters?.length ?? 0) > 0 && <button type="button" className="btn tiny pri" data-tool="recording-episode" disabled={cutting} onClick={() => { setCutting(true); void onEpisode().finally(() => setCutting(false)); }} title="A card with one scene per chapter, cut to this recording with its own sound and its words as the line">{cutting ? 'Cutting…' : 'Make an episode'}</button>}
        {recording.shareUrl && <a href={recording.shareUrl} target="_blank" rel="noopener" className="btn tiny">Open the page ↗</a>}
        <button type="button" className="btn tiny" onClick={onClose}>Close</button>
      </div>
      {readErr && <p className="err" style={{ margin: 0 }}>{readErr}</p>}
      {suggested && <p className="row" style={{ margin: 0, gap: 8, alignItems: 'center', fontSize: 13 }}><span className="sub-dim">The director would call it</span> <strong>{suggested}</strong> <button type="button" className="btn tiny pri" onClick={() => { setName(suggested); onRename(suggested); setSuggested(null); }}>Use this title</button> <button type="button" className="btn tiny" onClick={() => setSuggested(null)}>Keep mine</button></p>}
      {recording.summary && <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-2)' }} data-tool="recording-summary">{recording.summary}</p>}
      {(recording.chapters?.length ?? 0) > 0 && (
        <div className="row wrap" style={{ gap: 4 }} data-tool="recording-chapters">
          {recording.chapters!.map((c) => <button key={c.at} type="button" className="chip" onClick={() => seek(c.at)}><span className="sub-dim" style={{ fontVariantNumeric: 'tabular-nums' }}>{clock(c.at * 1000)}</span> {c.title}</button>)}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(220px, 1fr)', gap: 12 }}>
        {recording.video ? <video ref={video} src={recording.video} poster={recording.poster ?? undefined} controls autoPlay playsInline onTimeUpdate={(e) => setT(e.currentTarget.currentTime)} style={{ width: '100%', borderRadius: 8, background: '#000' }} />
          : <p className="sub-dim">The file is not ready to play yet.</p>}
        <div style={{ display: 'grid', gap: 4, alignContent: 'start', maxHeight: 420, overflowY: 'auto' }}>
          <div className="row" style={{ gap: 6, alignItems: 'baseline' }}>
            <strong style={{ fontSize: 12 }}>Transcript</strong>
            {recording.transcript.length > 0 && <button type="button" className="btn tiny" onClick={() => void navigator.clipboard?.writeText(transcriptText(recording.transcript)).catch(() => undefined)}>Copy</button>}
          </div>
          {recording.transcript.length === 0 ? <span className="sub-dim">No words on this take.</span> : recording.transcript.map((l, i) => (
            <button key={i} type="button" onClick={() => seek(l.at)}
                    style={{ all: 'unset', cursor: 'pointer', display: 'flex', gap: 8, padding: '3px 6px', borderRadius: 4, background: i === cur ? 'rgba(224,177,0,.18)' : 'transparent', fontSize: 12.5 }}>
              <span className="sub-dim" style={{ fontVariantNumeric: 'tabular-nums', flex: 'none' }}>{clock(l.at * 1000)}</span><span>{l.text}</span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
